/**
 * 统一Token管理器
 * 解决token处理不一致的问题
 */

export interface TokenInfo {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
  user?: any;
}

class TokenManager {
  private static instance: TokenManager;
  private tokenKey = 'auth_token';
  private refreshTokenKey = 'refresh_token';
  private userKey = 'user_info';
  private rememberMeKey = 'remember_me';

  private constructor() {}

  public static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  /**
   * 获取当前有效的token
   * 统一从localStorage或sessionStorage获取
   */
  public getToken(): string | null {
    try {
      // 优先从localStorage获取（记住我的情况）
      let token = localStorage.getItem(this.tokenKey);
      if (token) {
        console.log('Token from localStorage');
        return token;
      }

      // 然后从sessionStorage获取
      token = sessionStorage.getItem(this.tokenKey);
      if (token) {
        console.log('Token from sessionStorage');
        return token;
      }

      console.warn('No token found in storage');
      return null;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }

  /**
   * 获取refresh token
   */
  public getRefreshToken(): string | null {
    try {
      return (
        localStorage.getItem(this.refreshTokenKey) ||
        sessionStorage.getItem(this.refreshTokenKey)
      );
    } catch (error) {
      console.error('Error getting refresh token:', error);
      return null;
    }
  }

  /**
   * 获取用户信息
   */
  public getUser(): any {
    try {
      const userStr =
        localStorage.getItem(this.userKey) ||
        sessionStorage.getItem(this.userKey);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error getting user info:', error);
      return null;
    }
  }

  /**
   * 保存token信息
   */
  public setTokenInfo(tokenInfo: TokenInfo, rememberMe: boolean = false): void {
    try {
      const storage = rememberMe ? localStorage : sessionStorage;

      // 保存tokens
      storage.setItem(this.tokenKey, tokenInfo.accessToken);
      if (tokenInfo.refreshToken) {
        storage.setItem(this.refreshTokenKey, tokenInfo.refreshToken);
      }

      // 保存用户信息
      if (tokenInfo.user) {
        storage.setItem(this.userKey, JSON.stringify(tokenInfo.user));
      }

      // 保存记住我设置
      localStorage.setItem(this.rememberMeKey, rememberMe.toString());

      // 如果选择记住我，同时也在localStorage保存
      if (rememberMe) {
        localStorage.setItem(this.tokenKey, tokenInfo.accessToken);
        if (tokenInfo.refreshToken) {
          localStorage.setItem(this.refreshTokenKey, tokenInfo.refreshToken);
        }
        if (tokenInfo.user) {
          localStorage.setItem(this.userKey, JSON.stringify(tokenInfo.user));
        }
      }

      console.log('Token info saved successfully', {
        storage: rememberMe ? 'localStorage' : 'sessionStorage',
        hasToken: !!tokenInfo.accessToken,
        hasRefreshToken: !!tokenInfo.refreshToken,
        hasUser: !!tokenInfo.user,
      });
    } catch (error) {
      console.error('Error saving token info:', error);
    }
  }

  /**
   * 清除所有token信息
   */
  public clearAll(): void {
    try {
      // 清除localStorage
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.refreshTokenKey);
      localStorage.removeItem(this.userKey);
      localStorage.removeItem(this.rememberMeKey);

      // 清除sessionStorage
      sessionStorage.removeItem(this.tokenKey);
      sessionStorage.removeItem(this.refreshTokenKey);
      sessionStorage.removeItem(this.userKey);

      console.log('All token info cleared');
    } catch (error) {
      console.error('Error clearing token info:', error);
    }
  }

  /**
   * 检查token是否存在
   */
  public hasValidToken(): boolean {
    const token = this.getToken();
    return !!token && token.length > 0;
  }

  /**
   * 检查是否记住我
   */
  public isRememberMe(): boolean {
    try {
      const rememberMe = localStorage.getItem(this.rememberMeKey);
      return rememberMe === 'true';
    } catch (error) {
      return false;
    }
  }

  /**
   * 获取完整的认证信息
   */
  public getAuthInfo(): {
    token: string | null;
    refreshToken: string | null;
    user: any;
    isAuthenticated: boolean;
    rememberMe: boolean;
  } {
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();
    const user = this.getUser();
    const rememberMe = this.isRememberMe();

    return {
      token,
      refreshToken,
      user,
      isAuthenticated: !!token,
      rememberMe,
    };
  }

  /**
   * 调试信息
   */
  public getDebugInfo(): any {
    return {
      localStorage: {
        token: !!localStorage.getItem(this.tokenKey),
        refreshToken: !!localStorage.getItem(this.refreshTokenKey),
        user: !!localStorage.getItem(this.userKey),
        rememberMe: localStorage.getItem(this.rememberMeKey),
      },
      sessionStorage: {
        token: !!sessionStorage.getItem(this.tokenKey),
        refreshToken: !!sessionStorage.getItem(this.refreshTokenKey),
        user: !!sessionStorage.getItem(this.userKey),
      },
      current: this.getAuthInfo(),
    };
  }
}

// 导出单例实例
export const tokenManager = TokenManager.getInstance();

// 导出便捷方法
export const getToken = () => tokenManager.getToken();
export const getAuthInfo = () => tokenManager.getAuthInfo();
export const clearAuthInfo = () => tokenManager.clearAll();
export const hasValidToken = () => tokenManager.hasValidToken();
